import {
  addEntities,
  deleteEntities,
  getAllEntitiesApply,
  getEntity,
  selectAllEntities,
  setEntities,
  updateEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities'
import { Injectable } from '@angular/core'
import { createStore } from '@ngneat/elf'
import { CreateStockCommandParams } from '@efp/api'
import { Stock } from '../models/stock'
import { StockDao } from '../services/dao/stock.dao'
import { ArticleRepository } from './article.repository'
import { Article } from '../models/article'
import { Doka360ProjectSelectorService } from '../services/doka360-project-selector.service'

export const stockStore = createStore({ name: 'stock' }, withEntities<Stock>())

@Injectable({
  providedIn: 'root',
})
export class StockRepository {
  private hasLoadedAllForScope = false
  public readonly stocks$ = stockStore.pipe(selectAllEntities())

  constructor(
    private readonly stockDao: StockDao,
    private readonly articleRepository: ArticleRepository,
    private readonly doka360ProjectSelectorService: Doka360ProjectSelectorService
  ) {
    this.doka360ProjectSelectorService.currentDoka360ProjectUid$.subscribe(() => {
      this.hasLoadedAllForScope = false
    })
  }

  public async createStockWithArticles(params: CreateStockCommandParams): Promise<Stock | null> {
    params.doka360ProjectUid = this.doka360ProjectSelectorService.getCurrentDoka360ProjectUid()

    const stock = await this.stockDao.createStockWithArticles(params)
    stockStore.update(addEntities(stock))
    this.articleRepository.updateStockArticles(stock.id, stock.articles)

    return stock
  }

  public async fetchAllByScopeSelector(): Promise<Stock[]> {
    const currentDoka360ProjectUid =
      this.doka360ProjectSelectorService.getCurrentDoka360ProjectUid()

    /* Use cache only for standalone user scope,
       because cache invalidation is not automatically trigger by other user/client changes
    */
    if (!currentDoka360ProjectUid && this.hasLoadedAllForScope) {
      const cachedStocks = stockStore.query(
        getAllEntitiesApply({
          filterEntity: (e) => e.doka360ProjectUid === undefined,
        })
      )

      if (cachedStocks && cachedStocks.length > 0) {
        return cachedStocks
      }
    }

    let stocks = await this.stockDao.findAll(currentDoka360ProjectUid)
    const articlePromises = stocks.map(async (stock) => {
      const articles = await this.articleRepository.findAllByStockId(stock.id, true)
      stock.articles = articles

      return stock
    })
    stocks = await Promise.all(articlePromises)

    stockStore.update(setEntities(stocks))
    this.hasLoadedAllForScope = true

    return stocks
  }

  public async findAllRelatedPlanAndProjectNamesById(stockId: number): Promise<string[]> {
    return this.stockDao.findAllRelatedPlanAndProjectNamesById(stockId)
  }

  public async findOneById(id: number): Promise<Stock | undefined> {
    let stock = stockStore.query(getEntity(id))

    if (!stock) {
      stock = await this.stockDao.findOneByIdWithArticles(id)

      if (stock) {
        stockStore.update(upsertEntities(stock))
        this.articleRepository.updateStockArticles(stock.id, stock.articles)
      }
    } else {
      stock.articles = await this.articleRepository.findAllByStockId(stock.id, false)
    }

    return stock
  }

  public async createArticlesFromCatalogArticles(
    stockId: number,
    catalogArticles: Article[]
  ): Promise<void> {
    await this.articleRepository.createAll(catalogArticles, stockId)

    const stock = await this.findOneById(stockId)
    if (stock) {
      stock.articles = await this.articleRepository.findAllByStockId(stock.id, false)
    }
  }

  public async delete(stock: Stock): Promise<void> {
    await this.stockDao.delete(stock)
    stockStore.update(deleteEntities(stock.id))
  }

  public async updateName(stock: Stock, name: string): Promise<void> {
    await this.stockDao.updateName(stock, name)
    stockStore.update(updateEntities(stock.id, { name }))
  }
}
